import React, {useEffect, useState} from 'react';
import moment from "moment";
import PropTypes from 'prop-types';
import {Grid, MenuItem, Table, TableBody, TableCell, TableRow} from "@mui/material";
import ListingActionsSelect from "../../../../components/Product/Table/ListingActionsSelect";
import {FormattedMessage} from "react-intl";
import ToggleNavigation from "../../../../components/Toggle/ToggleNavigation";
import Toggle from "../../../../components/Toggle/Toggle";
import {LineItemStatus} from "../../../../utils/LineItemStatus";
import ListingEmptyState from "../../../../components/Product/Table/ListingEmptyState";
import ListingRowsSkeleton from "../../../../components/Product/Table/ListingRowsSkeleton";
import ListingPagination from "../../../../components/Product/Table/ListingPagination";
import ListingTableHead from "../../../../components/Product/Table/ListingTableHead";
import ArticleRow from "./ArticleRow/ArticleRow";
import ScanModal from "./ScanModal";

ArticlesList.propTypes = {
    retailer: PropTypes.object,
    filters: PropTypes.object,
    lineItems: PropTypes.array,
    loading: PropTypes.bool,
    isEmpty: PropTypes.bool,
    totalPages: PropTypes.number,
    openScanModal: PropTypes.bool,
    setOpenScanModal: PropTypes.func,
    resetData: PropTypes.func,
    itemWorkflowUpdate: PropTypes.func
};

function ArticlesList({retailer, filters, lineItems = [], loading = true, isEmpty = false, totalPages = 0, openScanModal = false, setOpenScanModal, resetData, itemWorkflowUpdate}) {
    const [baseFilteredItems, setBaseFilteredItems] = useState([])
    const [filteredLineItems, setFilteredLineItems] = useState([])
    const [actionValue, setActionValue] = useState("")
    const [toggleValue, setToggleValue] = useState(0)
    const [toggleFilterValue, setToggleFilterValue] = useState("all")
    const [isSelectAll, setIsSelectAll] = useState(false)

    const [currentPage, setCurrentPage] = useState(1)

    const tableHead = [
        {id: "article", numeric: false, disablePadding: true, label: <FormattedMessage id={"retailer.picking.itemList.header.article"}/>, hideOnMobile: true, width: "12%"},
        {id: "name", numeric: false, disablePadding: true, label: <FormattedMessage id={"retailer.picking.itemList.header.name"}/>, hideOnMobile: true, width: "26%"},
        {id: "order", numeric: false, disablePadding: true, label: <FormattedMessage id={"retailer.picking.itemList.header.order"}/>, hideOnMobile: true, width: "16%"},
        {id: "shippingMethod", numeric: false, disablePadding: true, label: <FormattedMessage id={"retailer.picking.itemList.header.shippingMethod"}/>, hideOnMobile: true, width: "18%"},
        {id: "price", numeric: false, disablePadding: true, label: <FormattedMessage id={"retailer.picking.itemList.header.price"}/>, hideOnMobile: true, width: "12%"},
        {id: "action", numeric: false, disablePadding: true, label: <FormattedMessage id={"retailer.picking.itemList.header.action"}/>, hideOnMobile: true, width: "16%"},
    ]

    const toggleFilterValuesArray = ["all", LineItemStatus.PENDING, LineItemStatus.RESERVED, LineItemStatus.TO_SHIP, LineItemStatus.RETURNING_RETAILER]

    const statusDot = (color) => (
        <span style={{display: 'block', width: '8px', height: '8px', borderRadius: '50%', backgroundColor: color, marginTop: '4px', marginLeft: '4px'}}/>
    )

    const getItemCountByStatus = (status) => {
        if ("all" === status) {
            return baseFilteredItems.length
        }

        return baseFilteredItems.filter(item => status === item.status).length
    }

    const onSelectAllClick = () => {

    }

    useEffect(() => {
        let result = [...lineItems];

        if (filters?.searchBarInput) {
            const query = filters.searchBarInput.toLowerCase();
            result = result.filter(item =>
                [
                    item.variation?.product?.sku,
                    item.variation?.barcode,
                    item.variation?.sku,
                    item.order?.displayExternalId,
                ].some(field => field?.toLowerCase().includes(query))
            );
        }

        if (filters?.shippingMethods?.length > 0) {
            result = result.filter(item =>
                filters.shippingMethods.includes(item.shippingMethod?.id ?? item.shippingMethod)
            );
        }

        const { startDate, endDate } = filters?.dateRange ?? {};
        if (startDate || endDate) {
            result = result.filter(item => {
                const date = moment(item.order?.createdAt);
                if (startDate && date.isBefore(startDate, 'day')) return false;
                if (endDate && date.isAfter(endDate, 'day')) return false;
                return true;
            });
        }

        setBaseFilteredItems(result);
    }, [filters, lineItems]);

    useEffect(() => {
        if ("all" === toggleFilterValue) {
            setFilteredLineItems([...baseFilteredItems]);
            return;
        }
        setFilteredLineItems(baseFilteredItems.filter(item => toggleFilterValue === item.status));
    }, [toggleFilterValue, baseFilteredItems]);

    return (
        <>
        {openScanModal && (
            <ScanModal
                open={openScanModal}
                handleClose={() => {
                    if (resetData) resetData()
                    setOpenScanModal(false)
                }}
                resetData={resetData ?? (() => {})}
                toBeScannedItems={filteredLineItems}
                itemWorkflowUpdate={itemWorkflowUpdate}
            />
        )}
        <Grid container direction={"column"}>
            <Grid container justifyContent={"space-between"} alignItems={"center"}>
                <Grid item>
                    <ListingActionsSelect
                        value={actionValue}
                        onChange={(event) => setActionValue(event.target.value)}
                        displayEmpty={true}
                        autoWidth={true}
                        disabled={false}
                    >
                        <MenuItem value="" disabled><FormattedMessage id={"retailer.picking.itemList.action.default"}/></MenuItem>
                        <MenuItem value="valid"><FormattedMessage id={"retailer.picking.itemList.action.valid"}/></MenuItem>
                        <MenuItem value="add_ticket"><FormattedMessage id={"retailer.picking.itemList.action.ticket"}/></MenuItem>
                    </ListingActionsSelect>
                </Grid>
                <Grid item>
                    <ToggleNavigation
                        value={toggleValue}
                        onChange={(value) => {
                            setToggleValue(value)
                            setToggleFilterValue(toggleFilterValuesArray[value])
                        }}
                    >
                        <Toggle key={"all"} icon={null} label={<FormattedMessage id={"retailer.picking.itemList.status.all"} />} count={getItemCountByStatus("all")}/>
                        <Toggle key={LineItemStatus.PENDING} icon={statusDot('#C37607')} label={<FormattedMessage id={"retailer.picking.itemList.status.pending"} />} count={getItemCountByStatus(LineItemStatus.PENDING)}/>
                        <Toggle key={LineItemStatus.RESERVED} icon={statusDot('#690192')} label={<FormattedMessage id={"retailer.picking.itemList.status.reserved"} />} count={getItemCountByStatus(LineItemStatus.RESERVED)}/>
                        {/* This could trigger many logistical problem it will stay disabled until further notice */}
                        {/*<Toggle key={LineItemStatus.TO_SHIP} icon={statusDot('#003cff')} label={<FormattedMessage id={"retailer.picking.itemList.status.to-ship"} />} count={getItemCountByStatus(LineItemStatus.TO_SHIP)}/>*/}
                        <Toggle key={LineItemStatus.RETURNING_RETAILER} icon={statusDot('#C45318')} label={<FormattedMessage id={"retailer.picking.itemList.status.returning-retailer"} />} count={getItemCountByStatus(LineItemStatus.RETURNING_RETAILER)}/>
                    </ToggleNavigation>
                </Grid>
            </Grid>

            <Grid item sx={{mt: "16px"}}>
                {isEmpty ?
                    <ListingEmptyState
                        titleId={"retailer.product.list.empty.title"}
                        descriptionId={"retailer.product.list.empty.description"}
                    />
                    :
                    <>
                        <Table sx={{width: '100%'}}>
                            <colgroup>
                                {tableHead.map((col) => (
                                    <col key={col.id} style={{width: col.width}}/>
                                ))}
                            </colgroup>
                            <ListingTableHead
                                mergeCheckboxInFirstCell={true}
                                rows={tableHead}
                                onSelectAllClick={onSelectAllClick}
                                isSelectAllChecked={isSelectAll}
                            />
                            <TableBody >
                                {loading &&
                                    <ListingRowsSkeleton/>
                                }
                                {!loading && !isEmpty &&
                                    filteredLineItems.map(item => {
                                        return (
                                            <ArticleRow article={item} itemWorkflowUpdate={itemWorkflowUpdate}/>
                                        );
                                    })
                                }
                                {isEmpty &&
                                    <TableRow>
                                        <TableCell colSpan={6} sx={{padding: '0 !important'}}>
                                            <ListingEmptyState
                                                titleId={"retailer.product.list.noResult.title"}
                                                descriptionId={"retailer.product.list.noResult.description"}
                                                ctaLabelId={"retailer.product.list.noResult.cta"}
                                                ctaVariant={"secondary"}
                                                onCtaClick={() => {}}
                                            />
                                        </TableCell>
                                    </TableRow>
                                }
                            </TableBody>
                        </Table>
                    </>
                }
            </Grid>
        </Grid>
        </>
    );
}

export default ArticlesList;